import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Select, Spin, Empty, Typography, Tag } from 'antd';
import { SearchOutlined, EnvironmentOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../assets/utils/useAuth';
import lib from '../../assets/utils/lib';
import { useUnifiedSearch, UNIFIED_SEARCH_TYPEAHEAD_DEBOUNCE_MS } from '../../hooks/useUnifiedSearch';
import debounce from 'lodash/debounce';
import { getFacilityPlanType } from '../facilities/facilityUtils';

const { Option } = Select;
const { Text } = Typography;

/**
 * Searchable Facility Select Component
 * 
 * Features:
 * - Searchable facility list
 * - Default value support (current facility)
 * - Mobile-optimized
 * - Prevents duplicate facilities
 * - Loading states
 * - Empty states
 */
export const SearchableFacilitySelect = ({
  value,
  onChange,
  placeholder = "Search and select facility...",
  disabled = false,
  style = {},
  size = "large",
  showSearch = true,
  allowClear = true,
  currentFacility = null, // The enrollee's current facility
  state = null,
  planType = null,
  className = "",
  ...props
}) => {
  const { user } = useAuth();
  const unified = useUnifiedSearch({ manageLoading: false });
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [error, setError] = useState(false);
  const fetchRef = useRef(0);

  // Remove duplicate facilities helper (defined early to avoid hoisting issues)
  const removeDuplicates = (facilityList) => {
    const seen = new Set();
    return facilityList.filter(facility => {
      const key = `${facility.facility_name}-${facility.facility_id || facility.id}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  // Initial load - fetch all facilities matching state/planType
  const fetchInitialFacilities = useCallback(async () => {
    setLoading(true);
    setError(false);
    
    try {
      const payload = { 
        state: state || user?.state, 
        plan_type: planType || user?.insurance_package 
      };
      
      const response = await lib.getProviders(payload);
      const data = response?.data;
      
      if (parseInt(data?.error) === 1) {
        setError(true);
        console.error('Error fetching facilities:', data?.message);
      } else {
        // Remove duplicates based on facility_name and facility_id
        const uniqueFacilities = removeDuplicates(data?.data || []);
        setFacilities(uniqueFacilities);
      }
    } catch (err) {
      console.error('Error fetching facilities:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [state, planType, user?.state, user?.insurance_package]); // eslint-disable-line react-hooks/exhaustive-deps

  // Backend search for facilities (state/planType not passed to searchProviders)
  const searchFacilities = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length === 0) {
      return;
    }

    if (searchTerm.length < 3) {
      return;
    }

    fetchRef.current += 1;
    const fetchId = fetchRef.current;
    setSearchLoading(true);
    setError(false);

    try {
      const out = await unified.execute({
        resource: 'facilities',
        search: searchTerm,
        page: 1,
        pageSize: 50,
        omitState: true,
      });

      if (fetchId !== fetchRef.current) {
        return;
      }

      if (!out.ok) {
        if (out.aborted) return;
        setError(true);
        console.error('Error searching facilities:', out.error || out.validation);
        setFacilities([]);
        return;
      }

      const uniqueFacilities = removeDuplicates(out.data || []);
      setFacilities(uniqueFacilities);
    } catch (err) {
      if (fetchId === fetchRef.current) {
        console.error('Error searching facilities:', err);
        setError(true);
        setFacilities([]);
      }
    } finally {
      if (fetchId === fetchRef.current) {
        setSearchLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `execute` from useUnifiedSearch is stable
  }, [unified.execute]);

  // Fetch initial facilities when component mounts or dependencies change (only if no search value)
  useEffect(() => {
    if (!searchValue) {
      fetchInitialFacilities();
    }
  }, [state, planType, fetchInitialFacilities, searchValue]);

  // Debounced search function
  const debouncedSearch = useMemo(
    () => debounce((searchTerm) => {
      searchFacilities(searchTerm);
    }, UNIFIED_SEARCH_TYPEAHEAD_DEBOUNCE_MS),
    [searchFacilities]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Use facilities directly (already filtered by backend search)
  const filteredFacilities = facilities;

  // Handle search - triggers backend search with debouncing
  const handleSearch = (value) => {
    setSearchValue(value);
    
    // Cancel any pending debounced calls
    debouncedSearch.cancel();
    
    // If search is cleared, reload initial facilities immediately
    if (!value || value.length === 0) {
      fetchInitialFacilities();
      return;
    }
    
    // For 1-2 characters, clear results but don't search yet
    if (value.length < 3) {
      setFacilities([]);
      return;
    }
    
    // For 3+ characters, trigger debounced search
    debouncedSearch(value);
  };

  // Handle selection
  const handleSelect = (value, option) => {
    onChange?.(value, option);
  };

  // Handle clear
  const handleClear = () => {
    setSearchValue('');
    if (debouncedSearch) {
      debouncedSearch.cancel();
    }
    fetchInitialFacilities();
    onChange?.(null);
  };


  // Render facility option - completely simple
  const renderFacilityOption = (facility) => {
    const isCurrentFacility = currentFacility && 
      (facility.facility_name === currentFacility || 
       facility.facility_id === currentFacility);
    const planLabel = getFacilityPlanType(facility);

    return (
      <Option
        key={`${facility.facility_name}-${facility.facility_id || facility.id}`}
        value={facility.facility_name}
        title={planLabel ? `${facility.facility_name} - ${planLabel.toUpperCase()}` : facility.facility_name}
        data-facility={facility}
      >
        <div style={{ 
          padding: '8px 12px',
          backgroundColor: isCurrentFacility ? '#f6ffed' : 'transparent'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center'
          }}>
            <Text style={{ 
              fontSize: '14px', 
              color: isCurrentFacility ? '#52c41a' : '#1890ff'
            }}>
              {facility.facility_name}
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {isCurrentFacility && (
                <Tag color="green" size="small">
                  Current
                </Tag>
              )}
              {planLabel ? (
                <Tag color="blue" size="small">
                  {planLabel.toUpperCase()}
                </Tag>
              ) : null}
            </div>
          </div>
        </div>
      </Option>
    );
  };

  // Loading state
  if (loading) {
    return (
      <Select
        placeholder="Loading facilities..."
        disabled
        style={style}
        size={size}
        className={className}
        suffixIcon={<Spin size="small" />}
        {...props}
      />
    );
  }

  // Error state
  if (error) {
    return (
      <Select
        placeholder="Error loading facilities. Click to retry."
        disabled
        style={style}
        size={size}
        className={className}
        onClick={fetchInitialFacilities}
        {...props}
      />
    );
  }

  return (
    <Select
      value={value}
      onChange={handleSelect}
      onSearch={handleSearch}
      onClear={handleClear}
      placeholder={placeholder}
      disabled={disabled}
      style={{ width: '100%', ...style }}
      size={size}
      showSearch={showSearch}
      allowClear={allowClear}
      filterOption={false} // Backend handles filtering
      notFoundContent={
        searchLoading ? (
          <Spin size="small" />
        ) : searchValue ? (
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`No facilities found for "${searchValue}"`}
            style={{ padding: '20px' }}
          />
        ) : (
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No facilities available"
            style={{ padding: '20px' }}
          />
        )
      }
      loading={searchLoading}
      dropdownStyle={{
        maxHeight: '400px',
        overflow: 'auto'
      }}
      className={className}
      {...props}
    >
      {filteredFacilities.map(renderFacilityOption)}
    </Select>
  );
};

/**
 * Mobile-optimized Facility Select with clean design
 */
export const MobileOptimizedFacilitySelect = (props) => {
  return (
    <SearchableFacilitySelect
      {...props}
      size="large"
      style={{
        minHeight: '48px',
        fontSize: '16px',
        ...props.style
      }}
      className={`mobile-facility-select ${props.className || ''}`}
    />
  );
};

/**
 * Hook for managing facility selection state
 */
export const useFacilitySelect = (initialValue = null) => {
  const [selectedFacility, setSelectedFacility] = useState(initialValue);
  const [facilityData, setFacilityData] = useState(null);

  const handleFacilityChange = (value, option) => {
    setSelectedFacility(value);
    setFacilityData(option?.data);
  };

  const resetFacility = () => {
    setSelectedFacility(null);
    setFacilityData(null);
  };

  return {
    selectedFacility,
    facilityData,
    handleFacilityChange,
    resetFacility,
    setSelectedFacility
  };
};

export default SearchableFacilitySelect;
